import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

const STORAGE_KEY = "webprofit.owner.setup.v1";

function readStored() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeStored(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // El onboarding sigue funcionando aunque localStorage esté bloqueado.
  }
}

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function setupSignals(center, assistant, manualDone) {
  const accounts = center?.finance?.accounts || [];
  const recurring = center?.finance?.recurring?.plans || [];
  const timeline = center?.timeline || [];
  const financeRows = timeline.filter((item) => String(item?.kind || "").startsWith("finance_"));
  const people = assistant?.clients || center?.people?.focus || [];
  const commitments = center?.life?.commitments || [];
  const linkedCommitment = commitments.some((item) => idOf(item?.clientId));
  const scheduledCommitment = commitments.some((item) => item?.scheduledStartAt);

  return {
    money: accounts.length > 0,
    history: financeRows.length >= 10 || Boolean(manualDone.history),
    recurring: recurring.some((item) => item?.effectiveStatus === "active" || item?.status === "active"),
    people: people.length >= 3 || Boolean(manualDone.people),
    followup: linkedCommitment || Boolean(manualDone.followup),
    calendar: scheduledCommitment || Boolean(manualDone.calendar),
    voice: Boolean(manualDone.voice),
    future: Boolean(manualDone.future),
  };
}

const stepMeta = [
  {
    id: "money",
    short: "Caja",
    title: "1. Decile a WebProfit dónde está tu plata",
    body: "Creá tus cajas reales: Mercado Pago, Lemon, banco, efectivo. No hace falta que estén perfectas todavía; primero necesitamos los lugares donde existe el dinero.",
    cta: "Crear / revisar caja",
    icon: AccountBalanceWalletRoundedIcon,
    accent: "#00e5ff",
  },
  {
    id: "history",
    short: "Historial",
    title: "2. Bajá tu historia de movimientos",
    body: "Importá el Excel limpio. El objetivo es que WebProfit empiece a conocer cómo entra y sale tu dinero sin que tengas que reconstruir dos años a mano.",
    cta: "Importar movimientos",
    icon: UploadFileRoundedIcon,
    accent: "#7c4dff",
  },
  {
    id: "recurring",
    short: "Mensuales",
    title: "3. Marcá lo que vuelve a pasar",
    body: "Convertí pagos y cobros reales en mensuales: alquiler, Render, WebProfit de un cliente, etc. Así Futuro sabe qué dinero viene y qué dinero se va, con fecha.",
    cta: "Crear mensual",
    icon: RepeatRoundedIcon,
    accent: "#ff2bd6",
  },
  {
    id: "people",
    short: "Clientes",
    title: "4. Cargá 3 personas que hoy importan",
    body: "No reconstruyas todo tu pasado. Empezá con tres casos reales: un cliente activo, alguien que te debe o paga mensual, y un prospecto que querés mover.",
    cta: "Cargar persona",
    icon: GroupsRoundedIcon,
    accent: "#ff8f00",
  },
  {
    id: "followup",
    short: "Qué sigue",
    title: "5. A cada relación importante, poné un próximo paso",
    body: "Una persona sin próximo paso se pierde. Anotá qué quedó: llamar, cobrar, enviar propuesta, entregar, reunirse. Ésta es la unión entre CRM y agenda.",
    cta: "Crear seguimiento",
    icon: EventAvailableRoundedIcon,
    accent: "#76ff03",
  },
  {
    id: "calendar",
    short: "Semana",
    title: "6. Organizá una semana real",
    body: "Poné horario y duración a lo importante. WebProfit necesita saber cuándo trabajás y cuánto tiempo queda libre para calcular capacidad freelancer de verdad.",
    cta: "Abrir calendario",
    icon: EventAvailableRoundedIcon,
    accent: "#00e676",
  },
  {
    id: "voice",
    short: "Voz",
    title: "7. Hacé una operación completa hablando",
    body: "Probá el bucle: persona → seguimiento → propuesta o mensual → guardar → siguiente orden. La voz tiene que ahorrarte formularios, no agregarte trabajo.",
    cta: "Probar voz ahora",
    icon: GraphicEqRoundedIcon,
    accent: "#ff1744",
  },
  {
    id: "future",
    short: "Futuro",
    title: "8. Mirá si tus números tienen sentido",
    body: "Revisá disponible, gastos esperados, cobros seguros, fecha de tensión y brecha. Si algo no cierra, volvés al bucle y completás el dato que falta.",
    cta: "Revisar Futuro",
    icon: TrendingUpRoundedIcon,
    accent: "#ffd600",
  },
];

export default function OwnerSetupOnboarding({ center, assistant, onAction }) {
  const [stored, setStored] = React.useState(() => readStored());
  const [open, setOpen] = React.useState(() => !readStored()?.dismissed);
  const [selectedId, setSelectedId] = React.useState(() => readStored()?.selectedId || "");

  const manualDone = stored?.manualDone || {};
  const signals = React.useMemo(() => setupSignals(center, assistant, manualDone), [center, assistant, manualDone]);
  const completedCount = stepMeta.filter((step) => signals[step.id]).length;
  const firstIncomplete = stepMeta.find((step) => !signals[step.id])?.id || stepMeta[stepMeta.length - 1].id;
  const activeId = selectedId || firstIncomplete;
  const activeIndex = Math.max(0, stepMeta.findIndex((step) => step.id === activeId));
  const active = stepMeta[activeIndex];
  const finished = completedCount === stepMeta.length;

  const persist = React.useCallback((next) => {
    setStored(next);
    writeStored(next);
  }, []);

  const select = (id) => {
    setSelectedId(id);
    const next = { ...stored, selectedId: id };
    persist(next);
  };

  const dismiss = () => {
    setOpen(false);
    persist({ ...stored, dismissed: true, selectedId: activeId });
  };

  const reopen = () => {
    setOpen(true);
    persist({ ...stored, dismissed: false, selectedId: firstIncomplete });
    setSelectedId(firstIncomplete);
  };

  const markDone = (id = active.id) => {
    const nextManual = { ...(stored?.manualDone || {}), [id]: true };
    const nextIndex = Math.min(stepMeta.length - 1, activeIndex + 1);
    const nextId = stepMeta[nextIndex].id;
    persist({ ...stored, manualDone: nextManual, dismissed: false, selectedId: nextId });
    setSelectedId(nextId);
  };

  const runAction = () => {
    if (active.id === "voice" || active.id === "future" || active.id === "calendar") markDone(active.id);
    onAction?.(active.id);
  };

  const AccentIcon = active.icon;

  return (
    <>
      <Button
        size="small"
        onClick={reopen}
        startIcon={<RocketLaunchRoundedIcon />}
        sx={{
          minHeight: 30,
          px: 1.05,
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 1000,
          color: finished ? "success.main" : "#071018",
          background: finished ? "rgba(46, 204, 113, .12)" : "linear-gradient(90deg,#00e5ff,#76ff03,#ffd600)",
          boxShadow: finished ? "none" : "0 0 0 0 rgba(0,229,255,.55)",
          animation: finished ? "none" : "ownerSetupTinyPulse 2.1s infinite",
          "@keyframes ownerSetupTinyPulse": {
            "0%": { boxShadow: "0 0 0 0 rgba(0,229,255,.55)" },
            "70%": { boxShadow: "0 0 0 10px rgba(0,229,255,0)" },
            "100%": { boxShadow: "0 0 0 0 rgba(0,229,255,0)" },
          },
          "&:hover": { filter: "brightness(1.05)" },
        }}
      >
        {finished ? "Puesta a punto lista" : `Puesta a punto · ${completedCount}/${stepMeta.length}`}
      </Button>

      {open ? (
        <Box
          sx={{
            position: "fixed",
            right: { xs: 12, md: 24 },
            bottom: { xs: 12, md: 22 },
            width: { xs: "calc(100vw - 24px)", sm: 460 },
            zIndex: 1500,
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            boxShadow: "0 28px 80px rgba(0,0,0,.32)",
          }}
        >
          <Box sx={{ p: 1.5, pb: 1.15, background: "linear-gradient(135deg,rgba(0,229,255,.10),rgba(124,77,255,.08),rgba(255,43,214,.06))" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Box>
                <Stack direction="row" spacing={.7} alignItems="center">
                  <Typography sx={{ fontWeight: 1000, fontSize: 14 }}>Puesta a punto</Typography>
                  <Chip size="small" label={`${completedCount}/${stepMeta.length}`} sx={{ height: 22, fontWeight: 1000 }} />
                </Stack>
                <Typography sx={{ mt: .25, color: "text.secondary", fontSize: 11.5, fontWeight: 750 }}>
                  Prepará el sistema una vez. Después repetís el mismo bucle.
                </Typography>
              </Box>
              <Tooltip title="Cerrar por ahora">
                <IconButton size="small" onClick={dismiss}><CloseRoundedIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={(completedCount / stepMeta.length) * 100}
              sx={{ mt: 1.15, height: 5, borderRadius: 99, bgcolor: "action.hover", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg,#00e5ff,#76ff03,#ffd600,#ff2bd6)" } }}
            />
          </Box>

          <Stack direction="row" spacing={.55} sx={{ px: 1.4, py: 1.15, overflowX: "auto", scrollbarWidth: "thin" }}>
            {stepMeta.map((step, index) => {
              const done = Boolean(signals[step.id]);
              const current = step.id === active.id;
              const StepIcon = step.icon;
              return (
                <Tooltip key={step.id} title={step.title}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => select(step.id)}
                    sx={{
                      appearance: "none",
                      border: 0,
                      cursor: "pointer",
                      flex: "0 0 auto",
                      minWidth: 54,
                      bgcolor: "transparent",
                      p: .2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        mx: "auto",
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        color: done ? "#06110b" : current ? "#071018" : "text.secondary",
                        background: done ? "#76ff03" : current ? step.accent : "action.hover",
                        border: "2px solid",
                        borderColor: current ? step.accent : done ? "#76ff03" : "divider",
                        boxShadow: current ? `0 0 0 0 ${step.accent}88` : "none",
                        animation: current && !done ? "ownerSetupBubblePulse 1.45s infinite" : "none",
                        "@keyframes ownerSetupBubblePulse": {
                          "0%": { transform: "scale(1)", boxShadow: `0 0 0 0 ${step.accent}88` },
                          "70%": { transform: "scale(1.08)", boxShadow: `0 0 0 12px ${step.accent}00` },
                          "100%": { transform: "scale(1)", boxShadow: `0 0 0 0 ${step.accent}00` },
                        },
                      }}
                    >
                      {done ? <CheckRoundedIcon sx={{ fontSize: 20 }} /> : <StepIcon sx={{ fontSize: 19 }} />}
                    </Box>
                    <Typography sx={{ mt: .45, fontSize: 9.5, fontWeight: current ? 1000 : 800, color: current ? "text.primary" : "text.secondary", whiteSpace: "nowrap" }}>
                      {index + 1}. {step.short}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>

          <Box sx={{ px: 1.7, pb: 1.7 }}>
            <Stack direction="row" spacing={1.1} alignItems="flex-start">
              <Box sx={{ width: 42, height: 42, flex: "0 0 auto", borderRadius: 2.2, display: "grid", placeItems: "center", color: "#071018", background: active.accent, boxShadow: `0 10px 30px ${active.accent}33` }}>
                <AccentIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 1000, fontSize: 17, lineHeight: 1.15, letterSpacing: "-.025em" }}>{active.title}</Typography>
                <Typography sx={{ mt: .65, color: "text.secondary", fontSize: 12.5, lineHeight: 1.45, fontWeight: 700 }}>{active.body}</Typography>
              </Box>
            </Stack>

            {finished ? (
              <Box sx={{ mt: 1.3, p: 1.1, borderRadius: 2.5, bgcolor: "rgba(118,255,3,.08)", border: "1px solid rgba(118,255,3,.35)" }}>
                <Typography sx={{ fontWeight: 1000, fontSize: 12.5 }}>Bucle listo</Typography>
                <Typography sx={{ mt: .25, color: "text.secondary", fontWeight: 800, fontSize: 11.5 }}>Capturá → vinculá → agendá → ejecutá → cobrá/pagá → revisá Futuro → repetí.</Typography>
              </Box>
            ) : null}

            <Stack direction="row" alignItems="center" spacing={.7} sx={{ mt: 1.35 }}>
              <Button
                size="small"
                variant="contained"
                onClick={runAction}
                sx={{
                  flex: 1,
                  minHeight: 40,
                  borderRadius: 2.2,
                  textTransform: "none",
                  fontWeight: 1000,
                  color: "#071018",
                  background: active.accent,
                  boxShadow: `0 0 0 0 ${active.accent}99`,
                  animation: signals[active.id] ? "none" : "ownerSetupCtaPulse 1.25s infinite",
                  "@keyframes ownerSetupCtaPulse": {
                    "0%": { transform: "scale(1)", boxShadow: `0 0 0 0 ${active.accent}99` },
                    "65%": { transform: "scale(1.018)", boxShadow: `0 0 0 13px ${active.accent}00` },
                    "100%": { transform: "scale(1)", boxShadow: `0 0 0 0 ${active.accent}00` },
                  },
                  "&:hover": { background: active.accent, filter: "brightness(1.04)" },
                }}
              >
                {signals[active.id] ? "Abrir de nuevo" : active.cta}
              </Button>
              {!signals[active.id] ? (
                <Button size="small" onClick={() => markDone()} sx={{ minWidth: 0, px: 1, textTransform: "none", fontWeight: 900 }}>Ya está</Button>
              ) : null}
            </Stack>

            <Stack direction="row" justifyContent="space-between" sx={{ mt: .8 }}>
              <Button disabled={activeIndex === 0} size="small" startIcon={<ArrowBackRoundedIcon />} onClick={() => select(stepMeta[Math.max(0, activeIndex - 1)].id)} sx={{ minWidth: 0, px: .5, fontWeight: 850 }}>Anterior</Button>
              <Button disabled={activeIndex === stepMeta.length - 1} size="small" endIcon={<ArrowForwardRoundedIcon />} onClick={() => select(stepMeta[Math.min(stepMeta.length - 1, activeIndex + 1)].id)} sx={{ minWidth: 0, px: .5, fontWeight: 850 }}>Siguiente</Button>
            </Stack>
          </Box>
        </Box>
      ) : null}
    </>
  );
}
